import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, planLabel } from "@/lib/AuthContext";
import { User, LayoutDashboard, ShieldCheck, LogOut, ChevronDown } from "lucide-react";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const isAuthed = !!user;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#080808]/80 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-brand" className="flex flex-col leading-none">
          <span className="text-[9px] sm:text-[10px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">
            Young Black Man Incorporated
          </span>
          <span className="font-display text-lg sm:text-xl md:text-2xl font-extrabold text-[#F4F4F0] tracking-tight mt-0.5">
            Studios
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10 text-sm text-[#A0A0A0]">
          <a href="#use-cases" data-testid="nav-link-use-cases" className="hover:text-[#F4F4F0] transition-colors">Use Cases</a>
          <a href="#pricing" data-testid="nav-link-pricing" className="hover:text-[#F4F4F0] transition-colors">Pricing</a>
          <a href="#faq" data-testid="nav-link-faq" className="hover:text-[#F4F4F0] transition-colors">FAQ</a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {isAuthed ? (
            <UserMenu user={user} onLogout={logout} />
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-signin"
                className="text-xs sm:text-sm text-[#A0A0A0] hover:text-[#F4F4F0] transition-colors px-2 sm:px-3 py-2"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                data-testid="nav-cta-get-started"
                className="bg-[#D4AF37] text-[#080808] font-bold text-xs sm:text-sm px-4 sm:px-5 py-2.5 sm:py-3 hover:bg-[#F0C84B] transition-colors whitespace-nowrap"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initial = (user.name?.[0] || user.email?.[0] || "U").toUpperCase();

  const handleLogout = async () => {
    await onLogout();
    setOpen(false);
    navigate("/");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-user-menu-button"
        className="flex items-center gap-2 sm:gap-3 border border-[#2A2A2A] hover:border-[#D4AF37] pl-2 pr-3 py-2 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="w-7 h-7 rounded-full bg-[#D4AF37] text-[#080808] font-bold text-xs flex items-center justify-center" data-testid="nav-user-avatar">
          {initial}
        </span>
        <span className="hidden sm:flex flex-col leading-none text-left">
          <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">{planLabel(user)}</span>
          <span className="text-xs text-[#F4F4F0] mt-0.5 max-w-[120px] truncate">{user.email}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[#A0A0A0] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="nav-user-menu-dropdown"
          className="absolute right-0 top-full mt-2 w-60 bg-[#0F0F0F] border border-[#2A2A2A] shadow-2xl py-2 z-50"
          role="menu"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Signed in as</p>
            <p className="text-sm text-[#F4F4F0] truncate">{user.email}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] mt-1">
              {planLabel(user)}{user.role === "owner" ? " · Owner" : ""}
            </p>
          </div>
          <MenuItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="user-menu-dashboard" onClick={() => setOpen(false)} />
          {user.role === "owner" && (
            <MenuItem to="/owner" icon={ShieldCheck} label="Owner Console" testId="user-menu-owner" onClick={() => setOpen(false)} />
          )}
          <button
            onClick={handleLogout}
            data-testid="user-menu-logout"
            role="menuitem"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#A0A0A0] hover:text-[#F4F4F0] hover:bg-[#121212] transition-colors border-t border-white/10"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function MenuItem({ to, icon: Icon, label, testId, onClick }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 text-sm text-[#F4F4F0] hover:bg-[#121212] transition-colors"
    >
      <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.7} /> {label}
    </Link>
  );
}
